import {Filter} from 'common/src/components/filters/CLFilter/CLFilter.types';
import apiV2Service from 'common/src/modules/api/api-v2.service';
import {Nodes} from 'common/src/modules/decisionTree/decisionTree.types';
import _map from 'lodash/map';
import _reduce from 'lodash/reduce';
import {DateTime} from 'luxon';
import {
  MetricValue,
  RemoteOptionalDimensions,
  RemoteOptionalMetrics,
} from '../analytics/analytics.types';
import extractCustomConversionsFromMetrics from '../analytics/utils/extractCustomConversionsFromMetrics';
import {
  toClientAvailableFilter,
  toClientAvailableMetricsForPreset,
  toClientGeneralStatistics,
  toLocalFlightControlInsights,
  toNodesAggregatedInsights,
} from './campaignAnalytics.dataConverter';
import {
  AvailableMetricsForPreset,
  CampaignInsightsData,
  InsightMetricsOption,
  RemoteAvailableFilter,
  RemoteAvailableMetricsForPreset,
  RemoteCampaignInsightsData,
  MetaCustomConversions,
  RemoteGeneralStatistics,
} from './campaignAnalytics.types';
import {
  toClientOptionalDimensions,
  toClientOptionalDimensionsForFilters,
  toClientOptionalInsightsDimensions,
  toClientOptionalMetrics,
} from '../analytics/analytics.dataConverters';
import {AvailableMetricWithTotal} from '../campaigns/store/insightsPage/insightsPage.types';
import dataSourcesApi from '../dataSources/dataSources.api';
import {DataSourceType} from '../dataSources/dataSources.types';
import {externalInsightsMetrics} from './campaignAnalytics.constants';
import _some from 'lodash/some';
import _filter from 'lodash/filter';

const BASE_PATH = 'CampaignAnalytics';

async function getPresets() {
  const {data} = await apiV2Service.executeGetRequest<InsightMetricsOption[]>(
    `${BASE_PATH}/GetPresets`,
  );

  return data;
}

async function addPreset(params: InsightMetricsOption) {
  const [customConversions, metrics] = extractCustomConversionsFromMetrics(params.metrics);
  const {data} = await apiV2Service.executePostRequest<number>(
    `${BASE_PATH}/AddPreset`,
    {},
    {...params, metrics, customConversions},
  );

  return data;
}

async function updatePreset(params: InsightMetricsOption) {
  const [customConversions, metrics] = extractCustomConversionsFromMetrics(params.metrics);
  const {data} = await apiV2Service.executePutRequest<void>(
    `${BASE_PATH}/UpdatePreset/${params.id}`,
    {},
    {...params, metrics, customConversions},
  );

  return data;
}

async function deletePreset(id: number) {
  return apiV2Service.executeDeleteRequest<void>(`${BASE_PATH}/DeletePreset/${id}`);
}

async function getAvailableFilters({
  metrics,
  isSocial,
  campaignId,
  campaignVersionId,
}: {
  isSocial: boolean;
  metrics: MetricValue[];
  campaignId: number;
  campaignVersionId: number;
}) {
  const [customConversions, allMetrics] = extractCustomConversionsFromMetrics(metrics);
  const {data: dimensions} = await apiV2Service.executePostRequest<RemoteAvailableFilter[]>(
    `${BASE_PATH}/GetAvailableFilters`,
    {},
    {
      metrics: allMetrics,
      customConversions,
      filters: [],
      isSocial,
      campaignId,
      campaignVersionId,
    },
  );

  return toClientAvailableFilter(dimensions);
}

function aggregateInsightsFromRaw({
  raw,
  nodes,
  nodesIdsForCampaignInsights,
  fallbackCreativeNodeId,
  metrics,
}: {
  raw: RemoteCampaignInsightsData;
  nodes: Nodes;
  nodesIdsForCampaignInsights: number[];
  fallbackCreativeNodeId: number;
  metrics: MetricValue[];
}): CampaignInsightsData {
  const result = toNodesAggregatedInsights({
    nodes,
    nodesIdsForCampaignInsights,
    insights: raw.insights,
    nodeInsights: raw.nodeInsights,
    campaignTotals: raw.totals,
    fallbackCreativeNodeId,
    metrics,
  });

  return {
    totals: toLocalFlightControlInsights(raw.totals, metrics),
    nodes: result,
  };
}

async function getInsights({
  nodes,
  nodesIdsForCampaignInsights,
  campaignId,
  from,
  to,
  nodeIds,
  fallbackCreativeNodeId,
  metrics,
  timezone,
  filters,
}: {
  nodes: Nodes;
  nodesIdsForCampaignInsights: number[];
  campaignId: number;
  from?: DateTime;
  to?: DateTime;
  nodeIds?: number[];
  fallbackCreativeNodeId: number;
  placementsIds?: number[];
  metrics: MetricValue[];
  timezone: string;
  filters: Filter[];
}): Promise<{data: CampaignInsightsData; raw: RemoteCampaignInsightsData}> {
  const params = {campaignId, nodeIds, from: '', to: ''};

  if (from) {
    params.from = from.setZone(timezone, {keepLocalTime: true}).toISO();
  }

  if (to) {
    params.to = to.setZone(timezone, {keepLocalTime: true}).toISO();
  }

  function getFiltersQuery(filters: Filter[]) {
    return _reduce(
      filters,
      (accumulator, filter) => {
        const {filterValue, selectedFilterOptions} = filter;

        return {
          ...accumulator,
          ...(selectedFilterOptions?.length && {
            [filterValue]: _map(selectedFilterOptions, 'value'),
          }),
        };
      },
      {},
    );
  }

  const [customConversions, allMetrics] = extractCustomConversionsFromMetrics(metrics);
  const {data: campaignInsights} =
    await apiV2Service.executePostRequest<RemoteCampaignInsightsData>(
      `${BASE_PATH}/GetAllInsightsV2`,
      {},
      {
        ...params,

        ...(!!filters?.length && {filters: getFiltersQuery(filters)}),
        metrics: allMetrics,
        customConversions,
      },
    );

  const data = aggregateInsightsFromRaw({
    raw: campaignInsights,
    nodes,
    nodesIdsForCampaignInsights,
    fallbackCreativeNodeId,
    metrics,
  });

  return {data, raw: campaignInsights};
}

async function getAvailableMetrics({
  selectedMetrics,
  isSocial,
}: {
  selectedMetrics: MetricValue[];
  isSocial: boolean;
}): Promise<AvailableMetricsForPreset> {
  const [customConversions, metrics] = extractCustomConversionsFromMetrics(selectedMetrics);

  const {data} = await apiV2Service.executePostRequest<RemoteAvailableMetricsForPreset>(
    `${BASE_PATH}/GetAvailableMetrics`,
    {},
    {metrics, customConversions, isSocial},
  );

  return toClientAvailableMetricsForPreset(data, selectedMetrics);
}

async function getAvailableFacebookCustomConversions(): Promise<MetaCustomConversions[]> {
  const {data} = await apiV2Service.executeGetRequest<MetaCustomConversions[]>(
    `${BASE_PATH}/GetAvailableFacebookCustomConvertions`,
  );

  return data;
}

async function getOptionalDimensions(campaignId: number, campaignVersionId: number) {
  const {data} = await apiV2Service.executeGetRequest<RemoteOptionalDimensions[]>(
    `${BASE_PATH}/GetOptionalViewBy`,
    {campaignId, campaignVersionId},
  );

  return toClientOptionalDimensions(data);
}

async function getCampaignOptionalFilters(campaignId: number, campaignVersionId: number) {
  const {data} = await apiV2Service.executeGetRequest<RemoteOptionalDimensions[]>(
    `${BASE_PATH}/GetCampaignOptionalFilters`,
    {campaignId, campaignVersionId},
  );

  return toClientOptionalDimensionsForFilters(data);
}

async function getCampaignCardSpecificOptionalViewBy({
  campaignId,
  nodeId,
  campaignVersionId,
}: {
  campaignId: number;
  nodeId: number;
  campaignVersionId: number;
}) {
  const {data} = await apiV2Service.executeGetRequest<RemoteOptionalDimensions[]>(
    `${BASE_PATH}/${campaignId}/version/${campaignVersionId}/node/${nodeId}/GetCampaignCardSpecificOptionalViewBy`,
  );

  return toClientOptionalInsightsDimensions(data);
}

async function getGeneralStatistics(campaignId: number): Promise<{
  activeSince: string;
  availableMetricsWithTotals: AvailableMetricWithTotal[];
}> {
  const {data} = await apiV2Service.executeGetRequest<RemoteGeneralStatistics>(
    `${BASE_PATH}/${campaignId}/GeneralStatistics`,
  );

  return {
    activeSince: data.ActiveSince,
    availableMetricsWithTotals: toClientGeneralStatistics(data),
  };
}

async function getCampaignOptionalMetrics({
  customMetricsOptions,
  hasMetaCustomConversions,
}: {
  hasMetaCustomConversions: boolean;
  customMetricsOptions?: Partial<Record<MetricValue, string[]>>;
}) {
  const [{data}, hasExternalAggregatedData] = await Promise.all([
    apiV2Service.executeGetRequest<RemoteOptionalMetrics[]>(`${BASE_PATH}/GetOptionalMetrics`),
    dataSourcesApi
      .getDataSources()
      .then((dataSources) => _some(dataSources, {type: DataSourceType.ExternalAggregatedData})),
  ]);

  const metrics = hasExternalAggregatedData
    ? data
    : _filter(data, ({value}) => !externalInsightsMetrics.includes(value));

  return toClientOptionalMetrics({
    data: metrics,
    hasMetaCustomConversions,
    customMetricsOptions,
  });
}

export default {
  aggregateInsightsFromRaw,
  getInsights,
  addPreset,
  getPresets,
  updatePreset,
  deletePreset,
  getAvailableFilters,
  getAvailableMetrics,
  getAvailableFacebookCustomConversions,
  getOptionalDimensions,
  getCampaignOptionalFilters,
  getCampaignCardSpecificOptionalViewBy,
  getGeneralStatistics,
  getCampaignOptionalMetrics,
};
